import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadAuthed, downloadErrorMessage } from '@/lib/downloadAuthed';

/**
 * Busy state and a clear error for the download buttons on one page
 * (Warren, 29 Oct 2026). Each button passes its own key, so "Export CSV"
 * shows busy while "Download PDF" stays clickable:
 *
 *   const dl = useAuthedDownload();
 *   <button disabled={dl.isBusy('pdf')} onClick={() => void dl.download('pdf', path, 'report.pdf')}>
 *   {dl.error && <div role="alert">{dl.error}</div>}
 *
 * A busy key ignores further clicks, and downloadAuthed itself shares one
 * request per target, so a double click never downloads twice. The error
 * is the reason the download failed (lib/download.ts), cleared by the next
 * download.
 */
export function useAuthedDownload() {
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const download = useCallback(async (key: string, target: string, fallbackFilename: string): Promise<boolean> => {
    if (busyRef.current.has(key)) return false;
    busyRef.current.add(key);
    setBusy(new Set(busyRef.current));
    setError(null);
    try {
      await downloadAuthed(target, fallbackFilename);
      return true;
    } catch (err) {
      const message = await downloadErrorMessage(err);
      if (mounted.current) setError(message);
      return false;
    } finally {
      busyRef.current.delete(key);
      if (mounted.current) setBusy(new Set(busyRef.current));
    }
  }, []);

  const isBusy = useCallback((key: string) => busy.has(key), [busy]);
  const clearError = useCallback(() => setError(null), []);

  return { download, isBusy, error, clearError };
}
